import { Button, FilledInput, Modal } from "@material-ui/core";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PuiTypography from "../../../components/elements/PuiTypography";
import { useInitText } from "../../../components/function/useInitText";
import { AppState } from "../../../root/RootReducer";
import { Folder } from "../../models/Timeline";
import { CreateDir, RenameDir } from "../../store/actions/DirectoryActions";
import { SetIsHandleKeyboard } from "../../store/actions/TimelineActions";
import styles from "./CreateDirectoryModal.module.css";

interface CreateDirectoryModalProps {
    openCreateFolderModal: boolean;
    setOpenCreateFolderModal: (openCreateFolderModal: boolean) => void;
    createdFolderId: number;
    editFolderId: number;
    editFolderName: string;
    setEditFolderName: (editFolderName: string) => void;
}

function CreateDirectoryModal({
    openCreateFolderModal,
    setOpenCreateFolderModal,
    createdFolderId,
    editFolderId,
    editFolderName,
    setEditFolderName,
}: CreateDirectoryModalProps) {
    const dispatch = useDispatch();

    // Storeからデータ取得
    const { user, isLoading, directoryList } = useSelector((state: AppState) => ({
        user: state.user.user,
        isLoading: state.directory.isLoading,
        directoryList: state.directory.directoryList,
    }));

    // フォルダー名変更するの場合、フォルダー名テキストボックスの初期表示
    // モーダルが閉じる時、フォルダー名をリセット
    useEffect(() => {
        if (openCreateFolderModal) {
            if (editFolderName) setFolderName(editFolderName);
        } else {
            setFolderName("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openCreateFolderModal]);

    // フォルダー名入力処理
    const [folderName, setFolderName] = useInitText();
    const handleChangeFolderName = (event: any) => {
        setFolderName(event.target.value);
    };

    // フォルダー名が重複した場合、ボタン非活性
    const [existFolderName, setExistFolderName] = useState<Folder | undefined>(undefined);
    useEffect(() => {
        // 新規作成・directoryリストで入力したフォルダ名があるかないかチェック
        if (!editFolderName && directoryList && directoryList.length > 0) {
            const checkFolderName = directoryList.find((dir) => dir.name === folderName);
            setExistFolderName(checkFolderName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderName]);

    // フォルダー作成ボタンを押下の場合、
    const [isFolderCreate, setIsFolderCreate] = useState(false);
    const handleClickCreateFolder = () => {
        if (user) {
            if (editFolderName === "") {
                // ディレクトリ作成APIコール
                dispatch(CreateDir(createdFolderId, folderName, Number(user.id)));
            } else {
                // ディレクトリ名変更APIコール
                dispatch(RenameDir(editFolderId, folderName, Number(user.id)));
            }
        }
        // 入力値リセット
        setFolderName("");
        // 編集値リセット
        setEditFolderName("");
        setIsFolderCreate(true);
    };

    useEffect(() => {
        if (!isLoading && isFolderCreate) {
            // モーダルを閉じる
            CloseCreateFolderModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isFolderCreate]);

    // handleConfirmイベントでダイアログボックスの非表示後、フォルダー作成・フォルダー名変更モーダル非表示フラグ
    const [closeFolderModal, isCloseFolderModal] = useState(false);

    // ダイアログボックスの非表示後、フォルダー作成・フォルダー名変更モーダル非表示
    useEffect(() => {
        if (closeFolderModal) {
            CloseCreateFolderModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closeFolderModal]);

    // モーダルを閉じる
    const CloseCreateFolderModal = () => {
        dispatch(SetIsHandleKeyboard(true)); // 編集する際に、キーボードによるvideoのコントロールを無効にする
        isCloseFolderModal(false);
        setOpenCreateFolderModal(false);
        setIsFolderCreate(false);
    };

    return (
        <>
            <Modal
                open={openCreateFolderModal}
                onClose={CloseCreateFolderModal}
                className={styles.createFolderModal}
            >
                <div className={styles.createFolderDiv}>
                    <PuiTypography>
                        {editFolderName === ""
                            ? t("新規フォルダーの作成")
                            : t("フォルダー名の編集")}
                    </PuiTypography>
                    <div className={styles.inputArea}>
                        <FilledInput
                            value={folderName}
                            id="FolderName"
                            placeholder={String(t("フォルダー名を入力してください"))}
                            autoComplete="off"
                            disableUnderline={true}
                            className={styles.folderCreateInputBox}
                            onChange={handleChangeFolderName}
                        />
                        <Button
                            disabled={
                                folderName.length === 0 ||
                                existFolderName !== undefined ||
                                editFolderName === folderName
                            }
                            className={styles.createrFolderBtn}
                            onClick={handleClickCreateFolder}
                        >
                            {editFolderName === "" ? t("作成") : t("変更")}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default CreateDirectoryModal;
